// WU-2.1 — persistent announcement banner stack (NOT a toast). Mounted in
// AppShell above the routed content. Level styles: info = copper accent bar,
// warning = amber (var(--color-warning)); tokens per conductor/DESIGN.md.
import type { AnnouncementRecord } from "../api/client";
import styles from "../announcements.module.css";

function postedLine(a: AnnouncementRecord): string {
  const when = new Date(a.createdAt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  return a.createdByName ? `${a.createdByName} · ${when}` : when;
}

// Dismiss buttons put the body into aria-label so screen-reader users can
// tell stacked banners apart. Bodies can run up to 500 chars — too long for
// an accessible name — so truncate to a short, still-distinct preview.
const ARIA_BODY_MAX = 80;

function truncateForAria(body: string): string {
  if (body.length <= ARIA_BODY_MAX) return body;
  return `${body.slice(0, ARIA_BODY_MAX).trimEnd()}…`;
}

export function AnnouncementBanner({
  announcements,
  onDismiss,
}: {
  announcements: AnnouncementRecord[];
  onDismiss: (id: string) => void;
}) {
  if (announcements.length === 0) return null;
  return (
    <div className={styles.stack}>
      {announcements.map((a) => (
        <div
          key={a.id}
          role="status"
          className={`${styles.banner} ${a.level === "warning" ? styles.warning : styles.info}`}
        >
          <div className={styles.content}>
            <p className={styles.body}>{a.body}</p>
            <span className={styles.meta}>{postedLine(a)}</span>
          </div>
          <button
            type="button"
            className={styles.dismiss}
            aria-label={`Dismiss announcement: ${truncateForAria(a.body)}`}
            onClick={() => onDismiss(a.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
